import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Typography } from "@mui/material";
import { City, Professional, Team } from "@syadem/ariane-js";
import { isEqual } from "lodash-es";
import { JSX, useEffect, useState } from "react";
import { useArianeApi } from "../../hooks/useArianeApi";
import { useI18n } from "../../hooks/useI18n";
import { theme } from "../../layout/Theme";
import StyledAutoComplete from "../mui/StyledAutoComplete";

type ProfessionalSearchResult<ProfessionalsOnly extends boolean> = ProfessionalsOnly extends true
  ? Professional
  : Professional | Team;

export function ProfessionalSearchField<ProfessionalsOnly extends boolean = false>({
  city,
  professional,
  professionalError,
  professionalsOnly,
  onProfessionalChange,
  onArianeError,
}: {
  city?: City;
  professional?: ProfessionalSearchResult<ProfessionalsOnly>;
  professionalError?: boolean;
  professionalsOnly?: ProfessionalsOnly;
  onProfessionalChange: (professional?: ProfessionalSearchResult<ProfessionalsOnly>) => void;
  onArianeError?: (error: unknown) => void;
}) {
  const { t } = useI18n();
  const arianeApi = useArianeApi();
  const [professionalOptions, setProfessionalOptions] = useState<(Professional | Team)[]>([]);
  const [professionalInput, setProfessionalInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async function () {
      if (city) {
        try {
          setIsLoading(true);
          const results = await arianeApi.professionals.searchByNameAndZipCodes(professionalInput, city.zip_code);
          setProfessionalOptions(
            professionalsOnly ? results.filter((result) => result.type === "professional") : results,
          );
          onArianeError?.(undefined);
        } catch (error) {
          onArianeError?.(error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setProfessionalOptions([]);
        if (professional !== undefined) {
          onProfessionalChange(undefined);
        }
      }
    })();
  }, [
    arianeApi.professionals,
    city,
    professional,
    professionalsOnly,
    onArianeError,
    onProfessionalChange,
    professionalInput,
  ]);

  return (
    <StyledAutoComplete
      open={open}
      onOpen={() => {
        if (professionalInput) setOpen(true);
      }}
      onClose={() => setOpen(false)}
      disabled={!city}
      options={professionalOptions}
      value={professional ?? null}
      filterOptions={(options) => options}
      onInputChange={(_event, newProfessionalInput) => {
        setProfessionalInput(newProfessionalInput);
        if (!newProfessionalInput) setOpen(false);
      }}
      onChange={(_event, newProfessional) => {
        if (professionalsOnly && newProfessional?.type === "team") return;
        onProfessionalChange((newProfessional ?? undefined) as ProfessionalSearchResult<ProfessionalsOnly> | undefined);
        if (!newProfessional) setOpen(false);
      }}
      getOptionLabel={renderLabel}
      testId="professional"
      required
      label={t("pro")}
      placeholder={`ex : ${t("pro")}`}
      error={professionalError || undefined}
      loading={isLoading}
      fullWidth
      renderOption={(props, option) => (
        <li data-testid="professionalOption" {...props}>
          {renderResult(option)}
        </li>
      )}
      isOptionEqualToValue={isEqual}
    />
  );
}

function renderLabel(result: Professional | Team) {
  return result.type === "professional" ? `${result.first_name} ${result.last_name}` : (result.name ?? "");
}

function renderResult(result: Professional | Team): JSX.Element {
  return (
    <Box display="flex" justifyContent="space-between" width="100%">
      <Typography flex="1 1 auto" fontSize="0.8rem">
        {result.type === "professional" ? `${result.first_name} ${result.last_name}` : result.name}
        <br />
        {result.address}
        {result.type === "professional" ? (
          <>
            <br />
            {`${result.zip_code} ${result.city?.toUpperCase()}`}
          </>
        ) : null}
      </Typography>
      <Box display="flex" alignItems="center" justifyContent="center">
        {result.type === "professional" ? (
          <PersonIcon sx={{ color: theme.palette.primary[500] }} />
        ) : (
          <GroupsIcon sx={{ color: theme.palette.primary[500] }} />
        )}
      </Box>
    </Box>
  );
}
